import { useEffect, useState } from "react";
import { Crown, Check, Loader2, Sparkles } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const FEATURES = ["Advanced search & filters", "Advanced AI matching", "Compatibility Lab", "Voice & video calling", "Contact requests", "Who viewed you", "Premium visibility & boost"];

export default function Subscription() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);

  const load = () => api.get("/subscription").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  if (!data) return <FullSpinner />;
  const sub = data.subscription;

  const applyCoupon = async (plan) => {
    if (!coupon.trim()) { setCouponInfo(null); return; }
    try { const { data } = await api.post("/coupons/validate", { code: coupon, plan }); setCouponInfo(data); toast.success(`Coupon applied — pay ₹${data.final_amount}`); }
    catch (e) { setCouponInfo(null); toast.error(apiError(e)); }
  };

  const upgrade = async (plan) => {
    setBusy(plan);
    try {
      const { data: order } = await api.post("/subscription/create-order", { plan, coupon_code: coupon || undefined });
      if (order.configured && order.razorpay_key_id) {
        const ok = await loadRazorpay();
        if (!ok) throw new Error("Unable to load payment gateway.");
        const rzp = new window.Razorpay({
          key: order.razorpay_key_id,
          amount: order.amount_paise,
          currency: "INR",
          name: "ManaJodi",
          description: `${plan} membership`,
          order_id: order.order.order_id,
          theme: { color: "#781826" },
          handler: async (resp) => {
            await verify(order.order.order_id, resp);
          },
          modal: { ondismiss: () => setBusy("") },
        });
        rzp.open();
      } else {
        // Razorpay keys not configured — architecture-ready. Confirm activation for testing.
        await verify(order.order.order_id, null);
      }
    } catch (e) { toast.error(apiError(e)); setBusy(""); }
  };

  const verify = async (orderId, resp) => {
    try {
      const payload = { order_id: orderId };
      if (resp) { payload.razorpay_payment_id = resp.razorpay_payment_id; payload.razorpay_signature = resp.razorpay_signature; }
      const { data: v } = await api.post("/subscription/verify", payload);
      await refresh(); load();
      toast.success(v.simulated ? "Membership activated (test mode — add Razorpay keys for live payments)" : "Payment successful! Membership active.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const cancel = async () => { try { await api.post("/subscription/cancel"); await refresh(); load(); toast.success("Subscription cancelled"); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="subscription-page">
      <div><h1 className="font-serif text-3xl font-bold text-[#58111A]">Membership</h1><p className="text-sm text-[#605255]">Simple, transparent pricing. No hidden tiers.</p></div>

      <div className="mj-card p-5 flex items-center justify-between">
        <div>
          <div className="text-sm text-[#605255]">Current Plan</div>
          <div className="font-serif text-2xl font-bold text-[#781826] capitalize flex items-center gap-2"><Crown className="w-5 h-5 text-[#C59B27]" /> {sub.lifetime ? "Lifetime" : sub.plan} {sub.status === "cancelled" && "(Cancelling)"}</div>
          {sub.expires_at && <div className="text-xs text-[#9E7B1B] mt-1">Renews/expires: {new Date(sub.expires_at).toLocaleDateString()}</div>}
        </div>
        {sub.plan === "premium" && !sub.lifetime && sub.status === "active" && <button onClick={cancel} className="btn-outline text-sm">Cancel</button>}
      </div>

      {!sub.lifetime && (
        <div className="mj-card p-4 flex flex-col sm:flex-row gap-2 items-center" data-testid="coupon-box">
          <input className="mj-input" placeholder="Have a coupon code?" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} data-testid="coupon-input" />
          <button onClick={() => applyCoupon("premium")} className="btn-outline shrink-0 text-sm">Apply</button>
          {couponInfo && <span className="text-sm text-emerald-700 font-semibold shrink-0">✓ {couponInfo.discount_pct}% off → ₹{couponInfo.final_amount}</span>}
        </div>
      )}

      {!sub.lifetime && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-6 rounded-2xl bg-gradient-to-b from-[#FAF5EE] to-white border-2 border-[#C59B27]">
            <span className="chip">Most Flexible</span>
            <h3 className="font-serif text-2xl font-semibold text-[#261A1C] mt-3 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#C59B27]" /> Premium</h3>
            <div className="mt-1"><span className="font-serif text-3xl font-bold text-[#58111A]">₹150</span><span className="text-sm text-[#605255]"> / month</span></div>
            <ul className="mt-4 space-y-1.5">{FEATURES.map((f) => <li key={f} className="text-sm text-[#261A1C] flex items-start gap-2"><Check className="w-4 h-4 text-[#15803D] mt-0.5" /> {f}</li>)}</ul>
            <button onClick={() => upgrade("premium")} disabled={busy || sub.plan === "premium"} data-testid="upgrade-premium" className="btn-primary w-full mt-5">{busy === "premium" ? <Loader2 className="w-4 h-4 animate-spin" /> : sub.plan === "premium" ? "Current Plan" : "Upgrade for ₹150/month"}</button>
          </div>
          <div className="p-6 rounded-2xl bg-[#3B0A12] text-white border border-[#C59B27]/40">
            <span className="chip w-fit">Best for Long-Term</span>
            <h3 className="font-serif text-2xl font-semibold mt-3 flex items-center gap-2"><Crown className="w-5 h-5 text-[#C59B27]" /> Lifetime</h3>
            <div className="mt-1"><span className="font-serif text-3xl font-bold text-[#E2C382]">₹2,000</span><span className="text-sm text-[#D8B4B9]"> one-time</span></div>
            <ul className="mt-4 space-y-1.5">{FEATURES.map((f) => <li key={f} className="text-sm text-[#E7CDCF] flex items-start gap-2"><Check className="w-4 h-4 text-[#C59B27] mt-0.5" /> {f}</li>)}</ul>
            <button onClick={() => upgrade("lifetime")} disabled={busy} data-testid="upgrade-lifetime" className="btn-gold w-full mt-5">{busy === "lifetime" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Get Lifetime for ₹2,000"}</button>
          </div>
        </div>
      )}

      <div className="mj-card p-5" data-testid="payment-history">
        <h2 className="font-serif text-lg font-semibold text-[#261A1C] mb-3">Payment History</h2>
        {data.payments.length === 0 ? <p className="text-sm text-[#605255]">No payments yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-[#9E7B1B] uppercase"><th className="py-2">Plan</th><th>Amount</th><th>Date</th><th>Transaction</th><th>Status</th></tr></thead>
              <tbody>
                {data.payments.map((p) => (
                  <tr key={p.id} className="border-t border-[#F1E9DA]">
                    <td className="py-2 capitalize">{p.plan}</td><td>₹{p.amount}</td>
                    <td>{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="font-mono text-xs">{p.transaction_id || "—"}</td>
                    <td><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.status === "paid" ? "text-emerald-700 bg-emerald-50" : "text-amber-700 bg-amber-50"}`}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!data.razorpay_key_id && <p className="text-xs text-[#9E7B1B] mt-3">Razorpay keys not configured — payments run in test mode. Add RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET to enable live payments.</p>}
      </div>
    </div>
  );
}
